import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import Logo from './assets/botibonga_logo.svg';

// Context for session management
const SessionContext = createContext();

const useSession = () => {
  const context = useContext(SessionContext);
  if (!context) {
    throw new Error('useSession must be used within SessionProvider');
  }
  return context;
};

const SessionProvider = ({ children }) => {
  const [sessionId, setSessionId] = useState(() => {
    return localStorage.getItem('wasender_session_id') || null;
  });
  const [whatsappDetails, setWhatsappDetails] = useState({ name: '', phone: '' });

  const saveSessionId = (id) => {
    localStorage.setItem('wasender_session_id', id);
    setSessionId(id);
  };

  const clearSession = () => {
    localStorage.removeItem('wasender_session_id');
    setSessionId(null);
  };

  return (
    <SessionContext.Provider value={{
      sessionId,
      saveSessionId,
      clearSession,
      whatsappDetails,
      setWhatsappDetails
    }}>
      {children}
    </SessionContext.Provider>
  );
};

// Snackbar Component
const Snackbar = ({ message, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 5000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className="fixed top-4 right-4 z-50 animate-slideIn">
      <div className="bg-red-500 text-white px-5 py-3 rounded-xl shadow-lg flex items-center gap-3 max-w-sm">
        <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span className="text-sm font-medium">{message}</span>
        <button onClick={onClose} className="ml-2 hover:opacity-70 transition-opacity">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
};

// Progress Indicator
const ProgressIndicator = ({ currentStep, totalSteps }) => {
  return (
    <div className="flex items-center justify-center gap-2 mb-8">
      {Array.from({ length: totalSteps }, (_, i) => (
        <React.Fragment key={i}>
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-all duration-300
              ${i + 1 <= currentStep
                ? 'bg-blue-500 text-white'
                : 'bg-gray-100 text-gray-400'}`}
          >
            {i + 1 <= currentStep - 1 ? (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            ) : (
              i + 1
            )}
          </div>
          {i < totalSteps - 1 && (
            <div
              className={`w-12 h-1 rounded-full transition-all duration-300
                ${i + 1 < currentStep ? 'bg-blue-500' : 'bg-gray-100'}`}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

// Step 1: Welcome
const WelcomeStep = ({ onNext }) => {
  return (
    <div className="text-center animate-fadeIn">
      <div className="flex items-center justify-center gap-4 mb-10">
        {/* WhatsApp Placeholder */}
        <div className="w-20 h-20 bg-gradient-to-br from-green-400 to-green-600 rounded-2xl flex items-center justify-center shadow-lg shadow-green-200">
          <svg className="w-10 h-10 text-white" fill="currentColor" viewBox="0 0 24 24">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
          </svg>
        </div>

        {/* Connection Arrow */}
        <div className="flex items-center">
          <div className="w-16 h-0.5 bg-gradient-to-r from-green-400 to-blue-500"></div>
          <svg className="w-6 h-6 text-blue-500 -ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
          </svg>
        </div>

        {/* Botibonga Placeholder */}
        {/* <div className="w-20 h-20 bg-gradient-to-br from-blue-400 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg shadow-blue-200">
          <span className="text-white font-bold text-xl">B</span>
        </div> */}
        <div className="w-20 h-20 bg-white rounded-2xl flex items-center justify-center shadow-lg shadow-blue-200">
          <img src={Logo} alt="Botibonga Logo" className="w-12 h-12" />
        </div>
      </div>

      <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-3">
        Connect WhatsApp to Botibonga
      </h1>
      <p className="text-gray-500 mb-10">
        Link your WhatsApp account in a few quick steps.
      </p>

      <button
        onClick={onNext}
        className="inline-flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white font-semibold px-8 py-3.5 rounded-xl transition-all duration-200 hover:shadow-lg hover:shadow-blue-200 hover:-translate-y-0.5"
      >
        Start
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
        </svg>
      </button>
    </div>
  );
};

// Step 2: WhatsApp Details
const DetailsStep = ({ onNext, onBack, onError }) => {
  const { whatsappDetails, setWhatsappDetails, saveSessionId } = useSession();
  const [loading, setLoading] = useState(false);
  const [name, setName] = useState(whatsappDetails.name);
  const [phone, setPhone] = useState(whatsappDetails.phone);

  const validatePhone = (number) => {
    return number.startsWith('+') && number.length >= 8;
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      onError('Please enter a name for this connection.');
      return;
    }

    if (!validatePhone(phone)) {
      onError('Please enter a valid phone number with country code (e.g., +1234567890).');
      return;
    }

    setLoading(true);
    setWhatsappDetails({ name, phone });

    try {
      const response = await fetch('https://n8nbotibonga.botibonga.com/webhook/d3ed1ea0-b219-4738-9388-34a58085c838', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          whatsappName: name.trim(),
          whatsappNumber: phone.trim(),
        }),
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }

      const data = await response.json();

      if (Array.isArray(data) && data[0]?.wasender_session_id) {
        saveSessionId(data[0].wasender_session_id);
        onNext();
      } else {
        throw new Error('Invalid response');
      }
    } catch (error) {
      onError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="animate-fadeIn">
      <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2 text-center">
        Your WhatsApp details
      </h1>
      <p className="text-gray-500 mb-8 text-center">
        Enter the number you want to connect.
      </p>

      <div className="space-y-5 max-w-sm mx-auto">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., My Business"
            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all duration-200 bg-gray-50 focus:bg-white"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Phone number</label>
          <input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+1234567890"
            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all duration-200 bg-gray-50 focus:bg-white"
          />
          <p className="text-xs text-gray-400 mt-2">Include country code (e.g., +1 for USA)</p>
        </div>
      </div>

      <div className="flex justify-center gap-3 mt-10">
        <button
          onClick={onBack}
          disabled={loading}
          className="px-6 py-3 border border-gray-200 text-gray-600 font-medium rounded-xl hover:bg-gray-50 transition-all duration-200 disabled:opacity-50"
        >
          Back
        </button>
        <button
          onClick={handleSubmit}
          disabled={loading}
          className="inline-flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white font-semibold px-8 py-3 rounded-xl transition-all duration-200 hover:shadow-lg hover:shadow-blue-200 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (
            <>
              <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Connecting...
            </>
          ) : (
            'Next'
          )}
        </button>
      </div>
    </div>
  );
};

// Step 3: QR Code
const QRCodeStep = ({ onNext, onBack, onError }) => {
  const { sessionId, clearSession } = useSession();
  const [qrCode, setQrCode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [countdown, setCountdown] = useState(30);
  const [canRefresh, setCanRefresh] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [deletingSession, setDeletingSession] = useState(false);

  const fetchQRCode = useCallback(async () => {
    if (!sessionId) return;

    setLoading(true);
    setCanRefresh(false);
    setCountdown(30);

    try {
      const response = await fetch(`https://n8nbotibonga.botibonga.com/webhook/d89d4657-497c-411f-b2d4-2bd2a0533b6f/${sessionId}`);

      if (!response.ok) {
        throw new Error('Failed to fetch QR code');
      }

      const svgText = await response.text();
      setQrCode(svgText);
    } catch (error) {
      onError('Unable to load the QR code. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [sessionId, onError]);

  useEffect(() => {
    fetchQRCode();
  }, [fetchQRCode]);

  useEffect(() => {
    if (loading) return;

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          setCanRefresh(true);
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [loading, qrCode]);

  const handleBack = async () => {
    setDeletingSession(true);
    try {
      await fetch(`https://n8nbotibonga.botibonga.com/webhook/57997c0a-d93e-4756-86fd-c2f51d909456/57997c0a-d93e-4756-86fd-c2f51d909456/${sessionId}`, {
        method: 'DELETE',
      });
      clearSession();
      onBack();
    } catch (error) {
      onError('Something went wrong. Please try again.');
    } finally {
      setDeletingSession(false);
    }
  };

  const handleFinish = async () => {
    setCheckingStatus(true);
    try {
      const response = await fetch(`https://n8nbotibonga.botibonga.com/webhook/6089d694-ff26-4fa5-a0b0-18f2454268f8/6089d694-ff26-4fa5-a0b0-18f2454268f8/${sessionId}`);

      if (!response.ok) {
        throw new Error('Failed to check status');
      }

      const data = await response.json();

      if (Array.isArray(data) && data[0]?.status === 'connected') {
        onNext();
      } else {
        onError('Your WhatsApp account is not yet connected. Please scan the QR code again.');
      }
    } catch (error) {
      onError('Unable to verify connection. Please try again.');
    } finally {
      setCheckingStatus(false);
    }
  };

  return (
    <div className="animate-fadeIn">
      <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2 text-center">
        Link your WhatsApp account
      </h1>
      <p className="text-gray-500 mb-8 text-center">
        Scan the QR code using WhatsApp on your phone.
      </p>

      <div className="grid md:grid-cols-2 gap-8 items-start">
        {/* Instructions */}
        <div className="bg-gray-50 rounded-2xl p-6 order-2 md:order-1">
          <h3 className="font-semibold text-gray-800 mb-4">How to scan</h3>
          <ol className="space-y-4">
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-semibold">1</span>
              <span className="text-gray-600">Open WhatsApp on your phone</span>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-semibold">2</span>
              <div className="text-gray-600">
                <span>On Android, tap </span>
                <span className="font-medium">Menu (⋮)</span>
                <span> · On iPhone, tap </span>
                <span className="font-medium">Settings (⚙️)</span>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-semibold">3</span>
              <div className="text-gray-600">
                <span>Tap </span>
                <span className="font-medium">Linked devices</span>
                <span>, then select </span>
                <span className="font-medium">Link device</span>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-semibold">4</span>
              <span className="text-gray-600">Scan the QR code to confirm</span>
            </li>
          </ol>
        </div>

        {/* QR Code */}
        <div className="order-1 md:order-2">
          <div className="bg-white border border-gray-100 rounded-2xl p-6 shadow-sm">
            <div className="aspect-square max-w-[280px] mx-auto flex items-center justify-center bg-gray-50 rounded-xl overflow-hidden">
              {loading ? (
                <div className="flex flex-col items-center gap-3">
                  <svg className="animate-spin w-8 h-8 text-blue-500" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span className="text-sm text-gray-500">Loading QR code...</span>
                </div>
              ) : qrCode ? (
                <div
                  className="w-full h-full flex items-center justify-center p-4"
                  dangerouslySetInnerHTML={{ __html: qrCode }}
                />
              ) : (
                <span className="text-gray-400">QR code unavailable</span>
              )}
            </div>

            {/* Timer */}
            <div className="mt-4 text-center">
              {!canRefresh ? (
                <div className="inline-flex items-center gap-2 text-sm text-gray-500">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Expires in {countdown}s
                </div>
              ) : (
                <span className="text-sm text-orange-500 font-medium">QR code expired</span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap justify-center gap-3 mt-10">
        <button
          onClick={handleBack}
          disabled={deletingSession || checkingStatus}
          className="px-6 py-3 border border-gray-200 text-gray-600 font-medium rounded-xl hover:bg-gray-50 transition-all duration-200 disabled:opacity-50"
        >
          {deletingSession ? 'Going back...' : 'Back'}
        </button>
        <button
          onClick={fetchQRCode}
          disabled={!canRefresh || loading}
          className="inline-flex items-center gap-2 px-6 py-3 border border-blue-200 text-blue-600 font-medium rounded-xl hover:bg-blue-50 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Refresh QR Code
        </button>
        <button
          onClick={handleFinish}
          disabled={checkingStatus || deletingSession}
          className="inline-flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white font-semibold px-8 py-3 rounded-xl transition-all duration-200 hover:shadow-lg hover:shadow-blue-200 disabled:opacity-50"
        >
          {checkingStatus ? (
            <>
              <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Checking...
            </>
          ) : (
            'Finish'
          )}
        </button>
      </div>
    </div>
  );
};

// Step 4: Success
const SuccessStep = () => {
  return (
    <div className="text-center animate-fadeIn">
      <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
        <svg className="w-10 h-10 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      </div>

      <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-3">
        Connection successful 🎉
      </h1>
      <p className="text-gray-500">
        Your WhatsApp account is now connected to Botibonga.
      </p>

      <div className="mt-10 p-6 bg-blue-50 rounded-2xl inline-block">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
            <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
            </svg>
          </div>
          <div className="text-left">
            <p className="text-sm text-gray-500">WhatsApp</p>
            <p className="font-semibold text-gray-800">Connected</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main App Component
const App = () => {
  const [step, setStep] = useState(1);
  const [error, setError] = useState(null);

  const showError = (message) => {
    setError(message);
  };

  const clearError = () => {
    setError(null);
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return <WelcomeStep onNext={() => setStep(2)} />;
      case 2:
        return (
          <DetailsStep
            onNext={() => setStep(3)}
            onBack={() => setStep(1)}
            onError={showError}
          />
        );
      case 3:
        return (
          <QRCodeStep
            onNext={() => setStep(4)}
            onBack={() => setStep(2)}
            onError={showError}
          />
        );
      case 4:
        return <SuccessStep />;
      default:
        return <WelcomeStep onNext={() => setStep(2)} />;
    }
  };

  return (
    <SessionProvider>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
        {error && <Snackbar message={error} onClose={clearError} />}

        <div className="max-w-4xl mx-auto px-4 py-8 md:py-16">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-sm border border-gray-100 mb-6">
              {/* <div className="w-6 h-6 bg-blue-500 rounded-md flex items-center justify-center">
                <span className="text-white font-bold text-xs">B</span>
              </div> */}
              <img src={Logo} alt="Botibonga Logo" className="w-6 h-6" />
              <span className="font-semibold text-gray-800">Botibonga</span>
            </div>
          </div>

          {/* Progress */}
          {step < 4 && <ProgressIndicator currentStep={step} totalSteps={4} />}

          {/* Step indicator text */}
          {step < 4 && (
            <p className="text-center text-sm text-gray-400 mb-8">
              Step {step} of 4
            </p>
          )}

          {/* Content Card */}
          <div className="bg-white rounded-3xl shadow-xl shadow-blue-100/50 border border-gray-100 p-6 md:p-10">
            {renderStep()}
          </div>

          {/* Footer */}
          <p className="text-center text-xs text-gray-400 mt-8">
            Secure connection powered by Botibonga
          </p>
        </div>
      </div>
    </SessionProvider>
  );
};

export default App;
